/**
 * Typewriter / reveal utility for the stream-recovery path.
 *
 * When the SSE connection drops mid-reply, the hook polls the server and feeds
 * recovered content through `revealText` so it appears progressively rather
 * than being dumped in all at once.
 *
 * @param targetText   The full text that should eventually be displayed.
 * @param alreadyShown How many characters are already visible (allows the
 *                     caller to start mid-string, e.g. partial content from a
 *                     previous poll tick).
 * @param onTick       Called on each animation tick with the cumulative string
 *                     that should be rendered.
 * @param onComplete   Optional callback fired when the animation finishes
 *                     naturally (all characters revealed without cancellation).
 * @returns A cancel function — call it to stop the animation (e.g. on unmount).
 */
export function revealText(
  targetText: string,
  alreadyShown: number,
  onTick: (text: string) => void,
  onComplete?: () => void,
): () => void {
  let cancelled = false;
  let shown = Math.min(alreadyShown, targetText.length);

  // Aim for roughly 60 chars/tick at ~60 fps → ~3600 chars/s.
  // For very long responses we scale up so total reveal time is capped at ~3s.
  const remaining = targetText.length - shown;
  const TICK_MS = 16; // ~60 fps via setInterval (rAF unavailable in workers)
  const MAX_REVEAL_MS = 3000;
  const charsPerTick = Math.max(1, Math.ceil(remaining / (MAX_REVEAL_MS / TICK_MS)));

  const handle = setInterval(() => {
    if (cancelled) {
      clearInterval(handle);
      return;
    }
    shown = Math.min(shown + charsPerTick, targetText.length);
    onTick(targetText.slice(0, shown));
    if (shown >= targetText.length) {
      clearInterval(handle);
      onComplete?.();
    }
  }, TICK_MS);

  return () => {
    cancelled = true;
    clearInterval(handle);
  };
}
